import React from "react";
import { Stack, Text, Divider } from "@mantine/core";
import usePermissions from "../../../hooks/usePermissions";
import { useSalon } from "../../../context/SalonContext";
import { SalonContextType } from "../../../types/context";
import { RequiredFormsSection } from "../../formTemplates/RequiredFormsSection";
import { ServiceMessageSettings } from "../../formTemplates/ServiceMessageSettings";
import { ServicePostAppointmentSection } from "../../servicePostAppointment/ServicePostAppointmentSection";
import { ServicePreAppointmentDocumentsSection } from "../../servicePreAppointment/ServicePreAppointmentDocumentsSection";
import TaxRateMultiSelect from "../../settings/TaxRateMultiSelect";
import { MessageSettings } from "../../../types/messageTemplates";
import { PendingRequiredForm } from "../../../types/formTemplates";
import {
  PostAppointmentSettings,
  PendingPostAppointmentMessage,
} from "../../../types/servicePostAppointment";
import { PendingPreAppointmentDocument } from "../../../types/servicePreAppointment";

interface AdditionalInformationStepProps {
  serviceId: string | null;
  selectedTaxRateIds: number[];
  onTaxRateChange: (ids: number[]) => void;
  isLoading?: boolean;
  messageSettings?: MessageSettings;
  onMessageSettingsChange?: (settings: MessageSettings) => void;
  pendingRequiredForms?: PendingRequiredForm[];
  onAddPendingForm?: (form: PendingRequiredForm) => void;
  onRemovePendingForm?: (tempId: string) => void;
  pendingPreAppointmentDocuments?: PendingPreAppointmentDocument[];
  onAddPendingPreAppointmentDocument?: (
    doc: PendingPreAppointmentDocument,
  ) => void;
  onRemovePendingPreAppointmentDocument?: (tempId: string) => void;
  postAppointmentSettings?: PostAppointmentSettings;
  onPostAppointmentSettingsChange?: (settings: PostAppointmentSettings) => void;
  pendingPostAppointmentMessages?: PendingPostAppointmentMessage[];
  onAddPendingPostAppointmentMessage?: (
    message: PendingPostAppointmentMessage,
  ) => void;
  onUpdatePendingPostAppointmentMessage?: (
    tempId: string,
    message: PendingPostAppointmentMessage,
  ) => void;
  onRemovePendingPostAppointmentMessage?: (tempId: string) => void;
}

export default function AdditionalInformationStep({
  serviceId,
  selectedTaxRateIds,
  onTaxRateChange,
  isLoading = false,
  messageSettings,
  onMessageSettingsChange,
  pendingRequiredForms,
  onAddPendingForm,
  onRemovePendingForm,
  pendingPreAppointmentDocuments,
  onAddPendingPreAppointmentDocument,
  onRemovePendingPreAppointmentDocument,
  postAppointmentSettings,
  onPostAppointmentSettingsChange,
  pendingPostAppointmentMessages,
  onAddPendingPostAppointmentMessage,
  onUpdatePendingPostAppointmentMessage,
  onRemovePendingPostAppointmentMessage,
}: AdditionalInformationStepProps) {
  const { businessInfo } = useSalon() as SalonContextType;
  const perms = usePermissions();
  // Every forms surface here gates on the /access forms permission, not vertical.
  const canViewClientForms =
    perms?.formsAndWaivers?.canViewClientForms === true;
  const canViewClientDocuments =
    perms?.salonDocuments?.canViewDocuments === true;
  const showRequiredForms = canViewClientForms && canViewClientDocuments;
  const canManageServices = perms?.settingsServices?.canManageServices === true;

  return (
    <Stack gap="lg" p="md">
      {showRequiredForms && (
        <>
          <RequiredFormsSection
            salonServiceId={serviceId ?? ""}
            pendingForms={!serviceId ? pendingRequiredForms : undefined}
            onAddPendingForm={!serviceId ? onAddPendingForm : undefined}
            onRemovePendingForm={!serviceId ? onRemovePendingForm : undefined}
          />
          <Divider />
          <ServicePreAppointmentDocumentsSection
            salonServiceId={serviceId ?? ""}
            readOnly={!canManageServices}
            pendingDocuments={
              !serviceId ? pendingPreAppointmentDocuments : undefined
            }
            onAddPendingDocument={
              !serviceId ? onAddPendingPreAppointmentDocument : undefined
            }
            onRemovePendingDocument={
              !serviceId ? onRemovePendingPreAppointmentDocument : undefined
            }
          />
          <Divider />
        </>
      )}

      {businessInfo?.id && (
        <>
          <ServiceMessageSettings
            salonServiceUuid={serviceId ?? ""}
            readOnly={!canManageServices}
            pendingSettings={!serviceId ? messageSettings : undefined}
            onPendingChange={!serviceId ? onMessageSettingsChange : undefined}
          />
          <Divider />
        </>
      )}

      {canViewClientForms && businessInfo?.id && (
        <>
          <ServicePostAppointmentSection
            salonServiceId={serviceId ?? ""}
            readOnly={!canManageServices}
            pendingSettings={!serviceId ? postAppointmentSettings : undefined}
            pendingMessages={
              !serviceId ? pendingPostAppointmentMessages : undefined
            }
            onPendingSettingsChange={
              !serviceId ? onPostAppointmentSettingsChange : undefined
            }
            onAddPendingMessage={
              !serviceId ? onAddPendingPostAppointmentMessage : undefined
            }
            onUpdatePendingMessage={
              !serviceId ? onUpdatePendingPostAppointmentMessage : undefined
            }
            onRemovePendingMessage={
              !serviceId ? onRemovePendingPostAppointmentMessage : undefined
            }
          />
          <Divider />
        </>
      )}

      <Stack gap="xs">
        <Text fw={600} size="sm">
          Tax Rates
        </Text>
        <TaxRateMultiSelect
          value={selectedTaxRateIds}
          onChange={onTaxRateChange}
          disabled={isLoading}
        />
      </Stack>
    </Stack>
  );
}
